<template>
    <UiModalMobile :is-open="isOpen" title="Filters" @close="$emit('close')">
        <template #content>
            <div>
                <!-- User Filter Section (only show if multiple users) -->
                <div v-if="availableUsers.length > 1" class="py-2">
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        User
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="user in availableUsers"
                            :key="user.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                :checked="isUserSelected(user)"
                                @change="toggleUser(user)"
                                class="rounded text-10x-orange form-checkbox"
                            />
                            <div
                                class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                            >
                                <Icon
                                    :icon="
                                        user.is_business ? 'business' : 'user'
                                    "
                                    class="w-3 h-3 text-slate-500 dark:text-slate-400"
                                />
                            </div>
                            <div class="flex flex-col items-start">
                                <span class="text-slate-700 dark:text-slate-300">
                                    {{ user.name }}
                                </span>
                                <span
                                    v-if="
                                        user.is_business && user.business_owner
                                    "
                                    class="text-slate-500 dark:text-slate-400 text-xs"
                                >
                                    Owner: {{ user.business_owner }}
                                </span>
                            </div>
                        </label>
                    </div>
                    <hr class="mt-6 mb-2" />
                </div>
                <!-- Card Filter Section -->
                <div class="py-2">
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Card
                    </h3>
                    <div class="">
                        <label
                            v-for="card in availableCards"
                            :key="card.connected_account_id"
                            class="flex items-center gap-3 py-3"
                            :class="{ 'opacity-50': isCardLocked(card) }"
                        >
                            <input
                                type="checkbox"
                                :checked="isCardSelected(card)"
                                :disabled="isCardLocked(card)"
                                @change="selectCard(card)"
                                class="rounded text-10x-orange form-checkbox disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <div class="flex items-center gap-2 min-w-0 flex-1">
                                <ImageLoader class="shrink-0">
                                    <template #image>
                                        <img
                                            :src="`${cdnUrl}/${card?.image?.path}/_small/${card?.image?.file_name}`"
                                            :alt="card?.title"
                                            class="w-6 h-auto rounded-sm"
                                        />
                                    </template>
                                    <template #error>
                                        <div
                                            class="w-8 h-5 bg-slate-100 dark:bg-slate-700 rounded-sm border border- slate-200 flex items-center justify-center"
                                        >
                                            <Icon
                                                icon="credit-card"
                                                class="w-3 h-3 text-slate-400"
                                            />
                                        </div>
                                    </template>
                                </ImageLoader>
                                <span class="flex items-center min-w-0">
                                    <span class="text-slate-700 dark:text-slate-300 truncate">
                                        {{ card.title }}
                                    </span>
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm ml-1 shrink-0"
                                    >
                                        ({{ card.last_four }})
                                    </span>
                                </span>
                                <!-- Lock icon for non-tracked cards -->
                                <Icon
                                    v-if="isCardLocked(card)"
                                    icon="lock"
                                    class="w-4 h-4 text-slate-400 ml-auto shrink-0"
                                />
                            </div>
                        </label>
                    </div>
                    <!-- Upgrade hint for locked cards (outside v-for) -->
                    <p
                        v-if="availableCards.some((c) => isCardLocked(c))"
                        class="text-xs text-slate-500 dark:text-slate-400 mt-2 flex items-center gap-1"
                    >
                        <Icon icon="lock" class="w-3 h-3" />
                        <span>Upgrade to track more cards</span>
                    </p>
                </div>
                <!-- Bank Filter Section (hidden for freemium users who can only track 1 card) -->
                <div
                    v-if="availableBanks.length > 1 && isSubscribed"
                    class="py-2"
                >
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Bank
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="bank in availableBanks"
                            :key="bank.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                :checked="isBankSelected(bank)"
                                @change="toggleBank(bank)"
                                class="rounded text-10x-orange form-checkbox"
                            />
                            <img
                                v-if="bank.logo_url"
                                :src="bank.logo_url"
                                :alt="bank.name"
                                class="w-5 h-5 rounded-full shrink-0 object-contain"
                            />
                            <span
                                v-else
                                class="w-5 h-5 rounded-full shrink-0 bg-slate-100 dark:bg-slate-700"
                            ></span>
                            <span class="text-slate-700 dark:text-slate-300">{{ bank.name }}</span>
                        </label>
                    </div>
                </div>
                <!-- Benefit Period Filter Section -->
                <div v-if="availablePeriodTypes.length > 1" class="py-2">
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Benefit period
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="periodType in availablePeriodTypes"
                            :key="periodType.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                :checked="isPeriodTypeSelected(periodType)"
                                @change="togglePeriodType(periodType)"
                                class="rounded text-10x-orange form-checkbox"
                            />
                            <span class="text-slate-700 dark:text-slate-300">
                                {{ periodType.name }}
                            </span>
                        </label>
                    </div>
                </div>
                <!-- Usage Filter Section -->
                <div class="py-2">
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Usage
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="usageType in usageTypes"
                            :key="usageType.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                :checked="isUsageTypeSelected(usageType)"
                                @change="toggleUsageTypeSelection(usageType)"
                                class="rounded text-10x-orange form-checkbox"
                            />
                            <span class="text-slate-700 dark:text-slate-300">
                                {{ usageType.name }}
                            </span>
                        </label>
                    </div>
                </div>
                <!-- Category Filter Section -->
                <div v-if="availableCategories.length > 1" class="py-2">
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Category
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="category in availableCategories"
                            :key="category.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                :checked="isCategorySelected(category)"
                                @change="toggleCategory(category)"
                                class="rounded text-10x-orange form-checkbox"
                            />
                            <span class="text-slate-700 dark:text-slate-300">
                                {{ category.name }}
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex gap-3">
                <button
                    :disabled="!hasActiveFilters"
                    class="btn-base btn-white flex-1"
                    @click="clearAllFilters"
                >
                    Clear all
                </button>
                <button
                    class="btn-base btn-primary flex-1"
                    @click="emit('close')"
                >
                    Apply
                </button>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    availableUsers: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['close']);

const store = useCardBenefitsStore();
const {
    benefitsByCard,
    selectedCards,
    selectedUsers,
    selectedUsageTypes,
    selectedBanks,
    selectedPeriodTypes,
    selectedCategories,
    hasActiveFilters,
    isSubscribed,
    trackedCardIds,
} = storeToRefs(store);

const availableCategories = computed(() => store.availableCategories);

const isCategorySelected = (category) =>
    selectedCategories.value.some((c) => c.id === category.id);
const toggleCategory = (category) => store.toggleCategoryFilter(category);
const cdnUrl = ref(window.cdnUrl);

// Check if a card is locked (not tracked) for freemium users
const isCardLocked = (card) => {
    if (isSubscribed.value) return false;
    return !trackedCardIds.value.includes(card.connected_account_id);
};

// Get available cards for filtering
const availableCards = computed(() => {
    const cards = new Map();

    if (benefitsByCard.value?.groups) {
        benefitsByCard.value.groups.forEach((group) => {
            if (
                group.card &&
                group.connected_account_id &&
                !cards.has(group.connected_account_id)
            ) {
                cards.set(group.connected_account_id, {
                    ...group.card,
                    connected_account_id: group.connected_account_id,
                    last_four: group.connected_account_last_four,
                    card_image: group.card.image,
                });
            }
        });
    }

    // Sort: tracked cards first, then locked cards
    return Array.from(cards.values()).sort((a, b) => {
        const aLocked = isCardLocked(a);
        const bLocked = isCardLocked(b);
        if (aLocked && !bLocked) return 1;
        if (!aLocked && bLocked) return -1;
        return 0;
    });
});

// Get available banks for filtering
const availableBanks = computed(() => {
    const banks = new Map();

    if (benefitsByCard.value?.groups) {
        benefitsByCard.value.groups.forEach((group) => {
            const bankId = group.card?.issuing_bank_id;
            const bankName = group.card?.issuing_bank_name;

            if (bankId && bankName && !banks.has(bankId)) {
                banks.set(bankId, {
                    id: bankId,
                    name: bankName,
                    logo_url: group.card?.issuing_bank_logo_url || null,
                });
            }
        });
    }

    return Array.from(banks.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
    );
});

// Period type display names mapping
const periodTypeDisplayNames = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    semi_annual: 'Semi-annual',
    annual: 'Annual',
    biennial: 'Every 2 years',
    triennial: 'Every 3 years',
    quadrennial: 'Every 4 years',
    quinquennial: 'Every 5 years',
    decennial: 'Every 10 years',
    statement: 'Per statement',
};

// Cycle length in months — used to sort the Period filter shortest → longest.
const periodMonths = {
    statement: 1,
    monthly: 1,
    quarterly: 3,
    semi_annual: 6,
    annual: 12,
    biennial: 24,
    triennial: 36,
    quadrennial: 48,
    quinquennial: 60,
    decennial: 120,
};

// Get available period types for filtering
const availablePeriodTypes = computed(() => {
    const periodTypes = new Map();

    if (benefitsByCard.value?.groups) {
        benefitsByCard.value.groups.forEach((group) => {
            group.benefits?.forEach((benefit) => {
                const periodType = benefit.benefit?.period_type?.toLowerCase();

                if (periodType && !periodTypes.has(periodType)) {
                    periodTypes.set(periodType, {
                        id: periodType,
                        name: periodTypeDisplayNames[periodType] || periodType,
                    });
                }
            });
        });
    }

    return Array.from(periodTypes.values()).sort(
        (a, b) =>
            (periodMonths[a.id] ?? Infinity) -
            (periodMonths[b.id] ?? Infinity)
    );
});

const isUserSelected = (user) => {
    return selectedUsers.value.some((selected) => selected.id === user.id);
};

const isCardSelected = (card) => {
    return selectedCards.value.some(
        (selected) =>
            selected.connected_account_id === card.connected_account_id
    );
};

const isBankSelected = (bank) => {
    return selectedBanks.value.some((selected) => selected.id === bank.id);
};

const toggleBank = (bank) => {
    store.toggleBankFilter(bank);
};

const isPeriodTypeSelected = (periodType) => {
    return selectedPeriodTypes.value.some(
        (selected) => selected.id === periodType.id
    );
};

const togglePeriodType = (periodType) => {
    store.togglePeriodTypeFilter(periodType);
};

const toggleUser = (user) => {
    store.toggleUserFilter(user);
};

const selectCard = (card) => {
    // Toggle the card selection in the store
    store.toggleCardFilter({
        connected_account_id: card.connected_account_id,
        title: card.title,
    });
};

// Usage types - static options (same as desktop). IDs stay 'redeemed' /
// 'not_redeemed' for store filter compatibility; only display names change.
const usageTypes = [
    { id: 'redeemed', name: 'Used' },
    { id: 'not_redeemed', name: 'Unused' },
];

// Usage type methods
const isUsageTypeSelected = (usageType) => {
    return selectedUsageTypes.value.some(
        (selected) => selected.id === usageType.id
    );
};

const toggleUsageTypeSelection = (usageType) => {
    store.toggleUsageFilter(usageType);
};

const clearAllFilters = () => {
    store.clearFilters();
};
</script>
